import { useEffect, useMemo, useState } from "react";
import { computeMonthlyReport, copy, PRODUCT_LINES } from "@wheels-crm/shared-types";
import type { Database, ProductLine } from "@wheels-crm/shared-types";
import { supabase } from "../lib/supabaseClient";
import { formatUsd } from "../lib/currency";
import { Button } from "../components/ui/Button";
import { FieldWrap, Input } from "../components/ui/Input";

type OrderRow = Database["public"]["Tables"]["orders"]["Row"];
type ReportScope = ProductLine | "all";
type DateBasis = "created" | "completed";

const REPORT_SCOPES: ReportScope[] = ["all", ...PRODUCT_LINES];
const DATE_BASES: DateBasis[] = ["created", "completed"];
const EXPENSE_FIELDS = ["rent", "salary", "taxes", "materials", "utilities"] as const;
type ExpenseField = (typeof EXPENSE_FIELDS)[number];
// rent/salary/taxes/materials/utilities are shared business overhead (only meaningful for
// Все разом). ad spend is bought separately per line, so it's tracked as two numbers and
// subtracted from that line's own profit even when viewing Диски or Рулі alone.
type ExpenseValues = Record<ExpenseField, string> & { adWheels: string; adSteering: string };

function emptyExpenses(): ExpenseValues {
  return { rent: "", salary: "", taxes: "", materials: "", utilities: "", adWheels: "", adSteering: "" };
}

function currentMonthValue(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function ReportsPage() {
  const [month, setMonth] = useState(currentMonthValue());
  const [scope, setScope] = useState<ReportScope>("all");
  const [dateBasis, setDateBasis] = useState<DateBasis>("created");
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState<ExpenseValues>(emptyExpenses());
  const [savingExpenses, setSavingExpenses] = useState(false);

  useEffect(() => {
    const [year, m] = month.split("-").map(Number);
    const start = new Date(year, m - 1, 1).toISOString();
    const end = new Date(year, m, 1).toISOString();
    // "По даті заявки" buckets an order into the month it was taken (createdatt) — matches the
    // Воронка funnel. "По даті виконання" buckets it into the month it was actually marked
    // done (completed_at), so revenue lines up with the month the production cost landed in —
    // an order taken on the 25th and finished next month counts toward next month's numbers.
    // Only 'done' orders have a completed_at, so that mode naturally excludes everything still
    // open or rejected (see the funnel-stats card below, which is hidden in that mode).
    const dateColumn = dateBasis === "completed" ? "completed_at" : "created_at";
    setLoading(true);
    supabase
      .from("orders")
      .select("*")
      .gte(dateColumn, start)
      .lt(dateColumn, end)
      .then(({ data }) => {
        setOrders(data ?? []);
        setLoading(false);
      });

    supabase
      .from("monthly_expenses")
      .select("*")
      .eq("month", month)
      .maybeSingle()
      .then(({ data }) => {
        setExpenses(
          data
            ? {
                rent: String(data.rent),
                salary: String(data.salary),
                taxes: String(data.taxes),
                materials: String(data.materials),
                utilities: String(data.utilities),
                adWheels: String(data.ad_spend_wheels),
                adSteering: String(data.ad_spend_steering_wheels),
              }
            : emptyExpenses(),
        );
      });
  }, [month, dateBasis]);

  const scopedOrders = useMemo(
    () => (scope === "all" ? orders : orders.filter((o) => o.product_line === scope)),
    [orders, scope],
  );

  // Same computation apps/companion-bot-worker and apps/scheduler-worker use for the
  // Telegram version of this report (packages/shared-types/src/monthlyReport.ts) — one
  // implementation, so the numbers can never drift between the UI and the bot (§6.9, §10).
  const stats = useMemo(() => computeMonthlyReport(scopedOrders), [scopedOrders]);

  const adWheels = Number(expenses.adWheels) || 0;
  const adSteering = Number(expenses.adSteering) || 0;

  const totalExpenses = useMemo(
    () => EXPENSE_FIELDS.reduce((sum, f) => sum + (Number(expenses[f]) || 0), 0) + adWheels + adSteering,
    [expenses, adWheels, adSteering],
  );
  const netProfit = stats.profit - totalExpenses;

  const lineAdSpend = scope === "wheels" ? adWheels : scope === "steering_wheels" ? adSteering : 0;
  const lineNetProfit = stats.profit - lineAdSpend;

  async function saveExpenses() {
    setSavingExpenses(true);
    await supabase.from("monthly_expenses").upsert({
      month,
      rent: Number(expenses.rent) || 0,
      salary: Number(expenses.salary) || 0,
      taxes: Number(expenses.taxes) || 0,
      materials: Number(expenses.materials) || 0,
      utilities: Number(expenses.utilities) || 0,
      ad_spend_wheels: adWheels,
      ad_spend_steering_wheels: adSteering,
    });
    setSavingExpenses(false);
  }

  return (
    <div>
      <div className="page-header">
        <h1>{copy.reports.title}</h1>
        <input
          type="month"
          className="input"
          style={{ width: "auto" }}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>

      <div className="product-line-switch">
        {REPORT_SCOPES.map((s) => (
          <button
            key={s}
            type="button"
            className={`product-line-tab ${scope === s ? "product-line-tab-active" : ""}`}
            onClick={() => setScope(s)}
          >
            {copy.productLine[s]}
          </button>
        ))}
      </div>

      <div className="product-line-switch">
        {DATE_BASES.map((b) => (
          <button
            key={b}
            type="button"
            className={`product-line-tab ${dateBasis === b ? "product-line-tab-active" : ""}`}
            onClick={() => setDateBasis(b)}
          >
            {b === "created" ? copy.reports.dateBasisCreated : copy.reports.dateBasisCompleted}
          </button>
        ))}
      </div>

      {loading ? (
        <p>Завантаження…</p>
      ) : (
        <>
          {dateBasis === "created" && (
            <div className="card" style={{ marginBottom: 16 }}>
              <p style={{ margin: 0 }}>
                Опрацьовано заявок: {stats.total} → {copy.leadsCategory.placed}: {stats.placed} ({stats.placedPct}%)
              </p>
              <p style={{ margin: "4px 0 0", color: "var(--color-text-muted)", fontSize: 13 }}>
                {copy.rejectReason.price}: {stats.rejectedPrice} · {copy.rejectReason.not_relevant}:{" "}
                {stats.rejectedNotRelevant}
              </p>
            </div>
          )}

          <div className="stat-grid">
            <div className="stat-card">
              <div className="value">{formatUsd(stats.revenue)}</div>
              <div className="label">{copy.reports.revenue}</div>
            </div>
            <div className="stat-card">
              <div className="value">
                {stats.extraSalesCount} / {formatUsd(stats.extraSalesSum)}
              </div>
              <div className="label">{copy.reports.extraSales}</div>
            </div>
            <div className="stat-card">
              <div className="value">{formatUsd(stats.cost)}</div>
              <div className="label">{copy.reports.cost}</div>
            </div>
            <div className="stat-card">
              <div className="value">{formatUsd(stats.delivery)}</div>
              <div className="label">{copy.reports.delivery}</div>
            </div>
            <div className="stat-card">
              <div className="value">{formatUsd(stats.profit)}</div>
              <div className="label">{copy.reports.profit}</div>
            </div>
            {scope === "all" && (
              <>
                <div className="stat-card">
                  <div className="value">{formatUsd(totalExpenses)}</div>
                  <div className="label">{copy.reports.expensesTotal}</div>
                </div>
                <div className="stat-card">
                  <div className="value">{formatUsd(netProfit)}</div>
                  <div className="label">{copy.reports.netProfit}</div>
                </div>
              </>
            )}
            {scope !== "all" && (
              <>
                <div className="stat-card">
                  <div className="value">{formatUsd(lineAdSpend)}</div>
                  <div className="label">{copy.reports.adSpend}</div>
                </div>
                <div className="stat-card">
                  <div className="value">{formatUsd(lineNetProfit)}</div>
                  <div className="label">{copy.reports.netProfitAfterAds}</div>
                </div>
              </>
            )}
          </div>

          {scope === "all" && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.reports.expensesTitle}</h2>
              <div className="expense-grid">
                {EXPENSE_FIELDS.map((f) => (
                  <FieldWrap key={f} label={copy.expenseField[f]} htmlFor={`expense-${f}`}>
                    <Input
                      id={`expense-${f}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={expenses[f]}
                      onChange={(e) => setExpenses((prev) => ({ ...prev, [f]: e.target.value }))}
                    />
                  </FieldWrap>
                ))}
                <FieldWrap label={`${copy.reports.adSpend} (${copy.productLine.wheels})`} htmlFor="expense-adWheels">
                  <Input
                    id="expense-adWheels"
                    type="number"
                    min="0"
                    step="0.01"
                    value={expenses.adWheels}
                    onChange={(e) => setExpenses((prev) => ({ ...prev, adWheels: e.target.value }))}
                  />
                </FieldWrap>
                <FieldWrap
                  label={`${copy.reports.adSpend} (${copy.productLine.steering_wheels})`}
                  htmlFor="expense-adSteering"
                >
                  <Input
                    id="expense-adSteering"
                    type="number"
                    min="0"
                    step="0.01"
                    value={expenses.adSteering}
                    onChange={(e) => setExpenses((prev) => ({ ...prev, adSteering: e.target.value }))}
                  />
                </FieldWrap>
              </div>
              <Button variant="primary" onClick={saveExpenses} disabled={savingExpenses}>
                {copy.reports.saveExpenses}
              </Button>
            </div>
          )}

          {scope !== "all" && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.reports.adSpend}</h2>
              <div className="expense-grid">
                <FieldWrap label={copy.reports.adSpend} htmlFor="expense-line-ad">
                  <Input
                    id="expense-line-ad"
                    type="number"
                    min="0"
                    step="0.01"
                    value={scope === "wheels" ? expenses.adWheels : expenses.adSteering}
                    onChange={(e) =>
                      setExpenses((prev) => ({
                        ...prev,
                        [scope === "wheels" ? "adWheels" : "adSteering"]: e.target.value,
                      }))
                    }
                  />
                </FieldWrap>
              </div>
              <Button variant="primary" onClick={saveExpenses} disabled={savingExpenses}>
                {copy.reports.saveExpenses}
              </Button>
            </div>
          )}

          <div className="card" style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.reports.topBrands}</h2>
            {stats.topBrands.length === 0 ? (
              <p className="empty-state">{copy.emptyState}</p>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {stats.topBrands.map(([brand, count]) => (
                  <li key={brand}>
                    {brand} — {count}
                  </li>
                ))}
              </ol>
            )}
          </div>

          <Button
            disabled
            title="Активується після підключення companion-bot-worker (Фаза 5, AGENTS.md §11)"
          >
            {copy.reports.sendToTelegram}
          </Button>
        </>
      )}
    </div>
  );
}
